import {
  KeyboardArrowDown as ArrowDownIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  Divider,
  InputAdornment,
  Paper,
  Popover,
  Popper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState, useRef, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Icon } from '@/components/common/Icon';
import { AddPropertyModal } from '@/components/dashboard/AddPropertyModal';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useAdminProperties } from '@/hooks/admin/useAdminProperties';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useRecentPropertyIds, MAX_RECENT_PROPERTIES } from '@/hooks/useRecentPropertyIds';
import { useUserRole } from '@/hooks/useUserRole';
import { UserProperty } from '@/models/Property';
import { AdminProperty, adminPropertyToUserProperty } from '@/services/api/admin';

const MAX_SEARCH_RESULTS = 15;

// inTile: the property row of the sidebar's context tile (house icon, address, switcher), with no deal line above it
export const PropertySelector = ({ collapsed = false, inTile = false }: { collapsed?: boolean; inTile?: boolean }) => {
  const { properties, selectedProperty, setSelectedProperty } = usePropertiesContext();
  // Owner mode is per-selected-property now (see ClientSidebar), so the
  // selector always surfaces the full list — owned + consideration —
  // letting a mixed-state user toggle between them.
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [addPropertyOpen, setAddPropertyOpen] = useState(false);
  const [popperOpen, setPopperOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const collapsedAnchorRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canViewAllProperties } = useUserRole();
  const { properties: adminProperties } = useAdminProperties({ enabled: canViewAllProperties });
  const { groups, isFetching: groupsFetching, refetch: refetchGroups } = useClientGroups();
  const { recentIds } = useRecentPropertyIds();
  const isIndependentOwnerMode =
    !canViewAllProperties &&
    !groupsFetching &&
    groups.length === 0 &&
    selectedProperty?.isOwned === true;

  // Refetch groups when the selected property has known associations that aren't in the
  // stale groups cache yet — prevents "No client assigned" / missing deal label.
  // Guard on !groupsFetching to avoid issuing a second request while one is already in-flight.
  const selectedKnownGroupIds = selectedProperty?.clientGroupIds ?? [];
  const selectedPid = selectedProperty?.property.id ?? '';
  const groupsMatchSelectedProperty =
    selectedKnownGroupIds.length === 0 ||
    groups.some(
      (g) => (g.propertyIds ?? []).includes(selectedPid) || selectedKnownGroupIds.includes(g.id),
    );
  useEffect(() => {
    if (selectedPid && !groupsMatchSelectedProperty && !groupsFetching) {
      refetchGroups();
    }
  }, [selectedPid, groupsMatchSelectedProperty, groupsFetching, refetchGroups]);

  const managementRoutes = [
    '/dashboard/properties',
    '/dashboard/properties-new',
    '/dashboard/clients',
    '/dashboard/inbox',
    '/dashboard/home-hub',
    '/dashboard/settings',
    '/dashboard/calculators',
  ];
  const isOnManagementPage = managementRoutes.some((route) => location.pathname.startsWith(route));

  const handleSelect = (property: UserProperty) => {
    setSelectedProperty(property);
    setAnchorEl(null);
    setPopperOpen(false);
    setSearchQuery('');
    if (isOnManagementPage) {
      navigate('/dashboard/summary');
    }
  };

  const handleAdminSelect = (adminProp: AdminProperty) => {
    handleSelect(adminPropertyToUserProperty(adminProp));
  };

  // Advisor/admin: compute search results or recently-viewed default list
  const adminPropertyMap = useMemo(
    () => new Map((adminProperties ?? []).map((p) => [p.id, p])),
    [adminProperties],
  );

  const advisorListItems = useMemo(() => {
    if (!canViewAllProperties) return [];
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      return (adminProperties ?? [])
        .filter(
          (p) =>
            p.address.toLowerCase().includes(q) ||
            p.city.toLowerCase().includes(q) ||
            p.state.toLowerCase().includes(q) ||
            p.zipCode.toLowerCase().includes(q),
        )
        .slice(0, MAX_SEARCH_RESULTS);
    }
    // Start with recently viewed, then pad with remaining properties up to the max.
    const recentSet = new Set(recentIds);
    const recent = recentIds.flatMap((id) => {
      const p = adminPropertyMap.get(id);
      return p ? [p] : [];
    });
    const remaining = (adminProperties ?? []).filter((p) => !recentSet.has(p.id));
    return [...recent, ...remaining].slice(0, MAX_RECENT_PROPERTIES);
  }, [canViewAllProperties, searchQuery, adminProperties, adminPropertyMap, recentIds]);

  const groupedProperties = useMemo(() => {
    if (canViewAllProperties || !properties || properties.length === 0) {
      return {
        groups: [] as { partnerName: string; status: string; properties: UserProperty[] }[],
        unassigned: properties ?? [],
      };
    }
    const propertyMap = new Map(properties.map((up) => [up.property.id, up]));
    const assignedIds = new Set<string>();
    const groupsList: { partnerName: string; status: string; properties: UserProperty[] }[] = [];
    for (const group of groups) {
      const groupProps = (group.propertyIds ?? [])
        .map((id) => propertyMap.get(id))
        .filter((up): up is UserProperty => up !== undefined);
      if (groupProps.length > 0) {
        groupProps.forEach((up) => assignedIds.add(up.property.id));
        groupsList.push({
          partnerName: group.holdingName,
          status: group.status,
          properties: groupProps,
        });
      }
    }
    const unassigned = properties.filter((up) => !assignedIds.has(up.property.id));
    return { groups: groupsList, unassigned };
  }, [canViewAllProperties, properties, groups]);

  // For advisors/admins the dropdown is always available (no properties list needed)
  if (!canViewAllProperties && (!properties || properties.length === 0)) return null;

  const open = Boolean(anchorEl);

  // ── Advisor / admin dropdown content (search + recently viewed) ─────────────
  const advisorDropdownContent = () => {
    const q = searchQuery.trim();
    const showRecent = !q && advisorListItems.length > 0;
    const showEmpty = !q && advisorListItems.length === 0;
    const sectionLabel = 'Recently viewed';

    return (
      <>
        <Box sx={{ px: '12px', pt: '8px', pb: '4px' }}>
          <TextField
            autoFocus
            size="small"
            placeholder="Search properties…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Icon name="search" size={14} style={{ color: 'var(--tx-faint)', display: 'block' }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: 13,
                color: 'var(--tx-text)',
                backgroundColor: 'var(--tx-fill)',
                borderRadius: '8px',
                alignItems: 'center',
                '& fieldset': { border: 0 },
                '&:hover': { backgroundColor: 'var(--tx-fill-2)' },
                '&.Mui-focused fieldset': { border: 0 },
              },
              '& .MuiInputAdornment-root': { marginTop: '0 !important' },
              '& input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
            }}
          />
        </Box>

        {showRecent && (
          <Box sx={{ px: '20px', pt: '10px', pb: '4px' }}>
            <Typography
              sx={{
                fontSize: 10,
                color: 'var(--tx-faint)',
              }}>
              {sectionLabel}
            </Typography>
          </Box>
        )}

        {showEmpty && (
          <Box sx={{ px: '20px', py: '12px' }}>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
              Search to find a property
            </Typography>
          </Box>
        )}

        {advisorListItems.map((p) => {
          const isSelected = selectedProperty?.property.id === p.id;
          return (
            <Box
              key={p.id}
              onClick={() => handleAdminSelect(p)}
              data-testid={`property-selector-item-${p.id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                height: 44,
                px: '12px',
                mx: '8px',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'background-color 0.12s',
                '&:hover': { backgroundColor: 'var(--tx-fill)' },
              }}>
              <Icon name="home" size={14} style={{ color: 'var(--tx-muted)', flexShrink: 0 }} />
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}>
                <Typography
                  noWrap
                  fontWeight={isSelected ? 600 : 500}
                  sx={{ fontSize: 13, lineHeight: 1.3, margin: 0, color: 'var(--tx-text)' }}>
                  {p.address}
                </Typography>
                <Typography
                  noWrap
                  sx={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', lineHeight: 1.2, margin: 0 }}>
                  {p.city}, {p.state} {p.zipCode}
                </Typography>
              </Box>
              {isSelected && <Icon name="check" size={14} style={{ color: 'var(--tx-text)', flexShrink: 0 }} />}
            </Box>
          );
        })}

        {q && advisorListItems.length === 0 && (
          <Box sx={{ px: '20px', py: '12px' }}>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
              No properties found
            </Typography>
          </Box>
        )}

        <Box
          onClick={() => {
            setAnchorEl(null);
            setPopperOpen(false);
            setAddPropertyOpen(true);
          }}
          data-testid="property-selector-add-btn"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            height: 44,
            px: '12px',
            mx: '8px',
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'background-color 0.12s',
            '&:hover': { backgroundColor: 'var(--tx-fill)' },
          }}>
          <Icon name="plus" size={14} style={{ color: 'var(--tx-muted)' }} />
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--tx-muted)',
            }}>
            Add property
          </Typography>
        </Box>
      </>
    );
  };

  // ── Shared dropdown content ─────────────────────────────────────────────────
  const propertyDropdownContent = () => (
    <>
      {/* Client group properties */}
      {groupedProperties.groups.map((group, groupIndex) => (
        <Box key={group.partnerName}>
          {groupIndex > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: '4px' }} />}
          {group.properties.map((userProperty) => {
            const isSelected = selectedProperty?.property.id === userProperty.property.id;
            return (
              <Box
                key={userProperty.property.id}
                onClick={() => handleSelect(userProperty)}
                data-testid={`property-selector-item-${userProperty.property.id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  height: 44,
                  px: '12px',
                  ml: '8px',
                  mr: '8px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'background-color 0.12s',
                  '&:hover': { backgroundColor: 'var(--tx-fill)' },
                }}>
                <Icon name="home" size={14} style={{ color: 'var(--tx-muted)', flexShrink: 0 }} />
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}>
                  <Typography
                    noWrap
                    fontWeight={isSelected ? 600 : 400}
                    sx={{ fontSize: 13, lineHeight: 1.3, margin: 0 }}>
                    {userProperty.property.address}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 11,
                      color: 'var(--tx-faint)',
                      lineHeight: 1.2,
                      margin: 0,
                    }}>
                    {userProperty.property.city}, {userProperty.property.state}{' '}
                    {userProperty.property.zipCode}
                  </Typography>
                </Box>
                {isSelected && <Icon name="check" size={14} style={{ color: 'var(--tx-text)', flexShrink: 0 }} />}
              </Box>
            );
          })}
        </Box>
      ))}

      {groupedProperties.groups.length > 0 && groupedProperties.unassigned.length > 0 && (
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
      )}

      {groupedProperties.unassigned.length > 0 && (
        <Box>
          {groupedProperties.groups.length > 0 && (
            <Box sx={{ px: 2, pt: 1.5, pb: 0.75 }}>
              <Typography
                variant="overline"
                sx={{ fontSize: 10, letterSpacing: 1.2, color: 'rgba(255,255,255,0.35)' }}>
                Other properties
              </Typography>
            </Box>
          )}
          {groupedProperties.unassigned.map((userProperty) => {
            const isSelected = selectedProperty?.property.id === userProperty.property.id;
            return (
              <Box
                key={userProperty.property.id}
                onClick={() => handleSelect(userProperty)}
                data-testid={`property-selector-item-${userProperty.property.id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  height: 44,
                  px: '12px',
                  mx: '8px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'background-color 0.12s',
                  '&:hover': { backgroundColor: 'var(--tx-fill)' },
                }}>
                <Icon name="home" size={14} style={{ color: 'var(--tx-muted)', flexShrink: 0 }} />
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}>
                  <Typography
                    noWrap
                    fontWeight={isSelected ? 600 : 400}
                    sx={{ fontSize: 13, lineHeight: 1.3, margin: 0 }}>
                    {userProperty.property.address}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 11,
                      color: 'var(--tx-faint)',
                      lineHeight: 1.2,
                      margin: 0,
                    }}>
                    {userProperty.property.city}, {userProperty.property.state}{' '}
                    {userProperty.property.zipCode}
                  </Typography>
                </Box>
                {isSelected && <Icon name="check" size={14} style={{ color: 'var(--tx-text)', flexShrink: 0 }} />}
              </Box>
            );
          })}
        </Box>
      )}

      {!isIndependentOwnerMode && (
        <Box
          onClick={() => {
            setAnchorEl(null);
            setAddPropertyOpen(true);
          }}
          data-testid="property-selector-add-btn"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            height: 44,
            px: '12px',
            mx: '8px',
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'background-color 0.12s',
            '&:hover': { backgroundColor: 'var(--tx-fill)' },
          }}>
          <Icon name="plus" size={14} style={{ color: 'var(--tx-muted)' }} />
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--tx-muted)',
            }}>
            Add property
          </Typography>
        </Box>
      )}
    </>
  );

  // ── Collapsed icon trigger ───────────────────────────────────────────────────
  if (collapsed) {
    return (
      <ClickAwayListener onClickAway={() => setPopperOpen(false)}>
        <Box ref={collapsedAnchorRef} sx={{ width: '100%' }}>
          <Tooltip title={popperOpen ? '' : 'Switch/Add Property'} placement="right">
            <Box
              onClick={() => setPopperOpen((prev) => !prev)}
              data-testid="property-selector-collapsed-trigger"
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: '9px',
                cursor: 'pointer',
                color: 'var(--tx-muted)',
                backgroundColor: 'transparent',
                borderRadius: '8px',
                mx: '6px',
                mb: '6px',
                transition: 'background 0.15s, color 0.15s',
                '&:hover': { backgroundColor: 'var(--tx-fill)' },
              }}>
              <SwapHorizIcon sx={{ fontSize: 16 }} />
            </Box>
          </Tooltip>

          <Popper
            open={popperOpen}
            anchorEl={collapsedAnchorRef.current}
            placement="right-start"
            sx={{ zIndex: 1300 }}
            modifiers={[{ name: 'offset', options: { offset: [-6, 10] } }]}>
            <Paper
              elevation={0}
              sx={{
                py: '8px',
                width: 300,
                maxHeight: 560,
                overflowY: 'auto',
                // the same Tradehaus .tx-pop as the expanded switcher: solid panel, sheen, deep soft shadow; never see-through
                backgroundColor: 'var(--tx-dialog)',
                backgroundImage: 'var(--tx-surface-light)',
                backdropFilter: 'var(--tx-blur-panel)',
                WebkitBackdropFilter: 'var(--tx-blur-panel)',
                borderRadius: '16px',
                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
                color: 'var(--tx-muted)',
              }}>
              {canViewAllProperties ? advisorDropdownContent() : propertyDropdownContent()}
            </Paper>
          </Popper>

          <AddPropertyModal open={addPropertyOpen} onClose={() => setAddPropertyOpen(false)} />
        </Box>
      </ClickAwayListener>
    );
  }

  const selectorContent = (
    <Box sx={{ pb: isMobile ? 0 : inTile ? '4px' : '20px' }}>
      {/* Trigger */}
      <ButtonBase
        onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
        data-testid="property-selector-trigger"
        sx={{
          width: '100%',
          mx: 0,
          mt: 0,
          px: isMobile ? '20px' : '10px',
          py: isMobile ? '10px' : '8px',
          // phone: one height whether a deal is picked (two lines) or not (one), so the page below starts in one place
          minHeight: isMobile ? 64 : undefined,
          borderRadius: '12px',
          backgroundColor: isMobile ? 'var(--tx-glass-panel)' : 'transparent',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: inTile ? '10px' : 1,
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: 'var(--tx-fill)' },
        }}>
        {inTile && (
          // a 28px slot, the avatar's width below it, so the icon and avatar share a centre line and the address and
          // the client's name start on one edge
          <Box sx={{ width: 28, flex: 'none', display: 'flex', justifyContent: 'center' }}>
            <Icon name="home" size={16} style={{ color: 'var(--tx-muted)' }} />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          {selectedProperty &&
            !inTile &&
            (() => {
              const pid = selectedProperty.property.id;
              const matchedGroup = groups?.find(
                (g) =>
                  (g.propertyIds ?? []).includes(pid) ||
                  (selectedProperty.clientGroupIds ?? []).includes(g.id),
              );
              const matchedDeal =
                matchedGroup?.deals?.find((d) => d.listings?.some((l) => l.propertyId === pid)) ||
                matchedGroup?.deals?.find((d) => d.status !== 'Closed');
              const dealName = matchedDeal?.name;
              const label = dealName ? `Deal: ${dealName}` : selectedProperty.property.name;
              return label ? (
                <Typography
                  noWrap
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'var(--tx-muted)',
                    lineHeight: 1.3,
                    mb: '2px',
                  }}>
                  {label}
                </Typography>
              ) : null;
            })()}
          <Typography
            noWrap
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 600,
              fontSize: 13,
              color: 'var(--tx-text)',
              lineHeight: inTile ? 1 : 1.35,
              letterSpacing: '-0.01em',
            }}>
            {selectedProperty ? (
              selectedProperty.property.address
            ) : (
              <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--tx-muted)' }}>
                Select property
              </span>
            )}
          </Typography>
          {/* in the tile: the city as the quiet second line, the same two-line stack as the client and deal below */}
          {inTile && selectedProperty && (
            <Typography
              noWrap
              sx={{ fontFamily: 'Manrope, sans-serif', fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', lineHeight: 1.2, mt: '3px' }}>
              {[selectedProperty.property.city, selectedProperty.property.state].filter(Boolean).join(', ')}
            </Typography>
          )}
        </Box>
        {inTile ? (
          <Icon name="unfold" size={16} style={{ color: 'var(--tx-faint)' }} />
        ) : (
        <ArrowDownIcon
          sx={{
            fontSize: 18,
            color: 'var(--tx-faint)',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
        )}
      </ButtonBase>

      {/* Dropdown panel */}
      {isMobile ? (
        open && (
          <Box
            sx={{
              // the same solid panel as the desktop popover (Tradehaus .tx-pop): on a phone the list opens over the
              // page, so a see-through wash let the content underneath read through it
              position: 'relative',
              zIndex: 2,
              mt: '6px',
              py: '8px',
              width: '100%',
              maxHeight: '60vh',
              overflowY: 'auto',
              backgroundColor: 'var(--tx-dialog)',
              backgroundImage: 'var(--tx-surface-light)',
              backdropFilter: 'var(--tx-blur-panel)',
              WebkitBackdropFilter: 'var(--tx-blur-panel)',
              borderRadius: '16px',
              boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
              boxSizing: 'border-box',
            }}>
            {canViewAllProperties ? advisorDropdownContent() : propertyDropdownContent()}
          </Box>
        )
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          marginThreshold={0}
          transitionDuration={0}
          slotProps={{
            paper: {
              sx: {
                mt: '3px',
                py: '8px',
                width: anchorEl?.offsetWidth ?? 300,
                maxHeight: 560,
                overflowY: 'auto',
                // Tradehaus .tx-pop: the theme's solid panel with its sheen and a deep, soft shadow; no see-through glass
                backgroundColor: 'var(--tx-dialog)',
                backgroundImage: 'var(--tx-surface-light)',
                backdropFilter: 'var(--tx-blur-panel)',
                WebkitBackdropFilter: 'var(--tx-blur-panel)',
                border: 'none',
                borderRadius: '16px',
                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
              },
            },
          }}>
          {canViewAllProperties ? advisorDropdownContent() : propertyDropdownContent()}
        </Popover>
      )}

      <AddPropertyModal open={addPropertyOpen} onClose={() => setAddPropertyOpen(false)} />
    </Box>
  );

  if (isMobile) {
    return (
      <ClickAwayListener onClickAway={() => setAnchorEl(null)}>{selectorContent}</ClickAwayListener>
    );
  }

  return selectorContent;
};
